import { CommonModule } from "@angular/common";
import { Component, Input } from "@angular/core";

import {
  Matriz,
  MatrizEvidencia,
  RiesgoProceso,
  RiesgoProcesoHistorial,
} from "../../shared/model/iso42001/matriz-riesgo.model";
import { FechaBitacoraPipe } from "../fecha-bitacora.pipe";
import { ExpedienteBase } from "../expediente-comun/expediente-base";
import { fechaPdf } from "../expediente-comun/expediente-formato";
import { PdfExpediente } from "../expediente-comun/pdf-expediente";

type PestanaExp = "resumen" | "riesgo" | "seguimiento" | "evidencias" | "bitacora";

/**
 * Expediente de un riesgo del Plan de Gestión.
 *
 * Es el equivalente del Expediente del activo para las áreas que NO trabajan
 * con activos: ahí la unidad de trazabilidad no es un sistema de IA, es el
 * riesgo del proceso. Reúne su evaluación, el plan de acción, los cortes de
 * monitoreo, las evidencias adjuntas y la bitácora, y lo imprime en PDF como
 * evidencia para la certificación.
 *
 * Todo es de SOLO LECTURA: se captura en el Plan de Gestión y las evidencias
 * se suben desde la sección Evidencias de cada riesgo.
 */
@Component({
  selector: "app-expediente-riesgo-ia",
  standalone: true,
  templateUrl: "./expediente-riesgo-ia.component.html",
  styleUrls: ["./expediente-riesgo-ia.component.css"],
  imports: [CommonModule, FechaBitacoraPipe],
})
export class ExpedienteRiesgoIAComponent extends ExpedienteBase {
  @Input() riesgo!: RiesgoProceso;
  /** La matriz a la que pertenece: da su nombre y si maneja activos. */
  @Input() matriz: Matriz | null = null;

  pestana: PestanaExp = "resumen";
  readonly secciones: { clave: PestanaExp; titulo: string }[] = [
    { clave: "resumen", titulo: "Resumen" },
    { clave: "riesgo", titulo: "El riesgo" },
    { clave: "seguimiento", titulo: "Monitoreo" },
    { clave: "evidencias", titulo: "Evidencias" },
    { clave: "bitacora", titulo: "Bitácora" },
  ];

  evidencias: MatrizEvidencia[] = [];
  /** "NO" cuando el riesgo declaró que las evidencias no aplican. */
  evidenciasAplica = "";
  cargandoEvd = false;

  historial: RiesgoProcesoHistorial[] = [];
  cargandoHist = false;
  errorHist = "";

  protected cargar(): void {
    const id = this.riesgo.idRiesgoProceso;
    if (!id) {
      return;
    }

    this.cargandoEvd = true;
    this.matrizServ.consultarEvidencias("RIESGO_PROCESO", id).subscribe({
      next: (res) => {
        this.cargandoEvd = false;
        this.evidencias = (res && !res.codigoError && res.evidencias) || [];
        this.evidenciasAplica = (res && !res.codigoError && res.aplica) || "";
        this.cdr.markForCheck();
      },
      error: () => {
        this.cargandoEvd = false;
        this.cdr.markForCheck();
      },
    });

    this.cargandoHist = true;
    this.matrizServ.historialRiesgo(id).subscribe({
      next: (res) => {
        this.cargandoHist = false;
        if (!res || res.codigoError) {
          this.errorHist = (res && res.mensajeError) || "No se pudo consultar la bitácora.";
        } else {
          this.historial = res.historial || [];
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.cargandoHist = false;
        this.errorHist = "No se pudo consultar la bitácora.";
        this.cdr.markForCheck();
      },
    });
  }

  /* ---------- Presentación ---------- */

  elegir(p: PestanaExp): void {
    this.pestana = p;
  }

  get nombreMatriz(): string {
    return (this.matriz && this.matriz.nombre) || "Matriz de riesgos";
  }

  get cortes(): any[] {
    return this.riesgo.seguimientos || [];
  }

  get cortesSinResultado(): number {
    return this.cortes.filter((s) => !s.resultado || !String(s.resultado).trim()).length;
  }

  /** Los folios de activos que el riesgo cita, si es que cita alguno. */
  get activosCitados(): string[] {
    const texto = (this.riesgo.activosRelacionados || "").trim();
    if (!texto) {
      return [];
    }
    return texto.split(",").map((x) => x.trim()).filter((x) => !!x);
  }

  conteoPestana(clave: PestanaExp): string {
    switch (clave) {
      case "seguimiento": return String(this.cortes.length);
      case "evidencias": return String(this.evidencias.length);
      case "bitacora": return String(this.historial.length);
      default: return "";
    }
  }

  /** Las bandas de severidad usan otros nombres que las de activos. */
  claseBanda(clasificacion?: string): string {
    switch (clasificacion) {
      case "Crítico":
      case "Critico": return "r-muyalto";
      case "Alta":
      case "Alto": return "r-alto";
      case "Media":
      case "Medio": return "r-medio";
      case "Baja":
      case "Bajo": return "r-bajo";
      default: return "";
    }
  }

  /** La clasificación viaja sin acento ("Critico"); en pantalla va con él. */
  etiquetaBanda(c?: string): string {
    return c === "Critico" ? "Crítico" : c || "—";
  }

  /** "Corte del 13/07/2026", si la evidencia respalda uno concreto. */
  corteDe(evd: MatrizEvidencia): string {
    if (!evd.fechaSeguimiento) {
      return "";
    }
    const f = String(evd.fechaSeguimiento);
    const soloDia = /^(\d{4})-(\d{2})-(\d{2})/.exec(f);
    if (soloDia) {
      return "Corte del " + soloDia[3] + "/" + soloDia[2] + "/" + soloDia[1];
    }
    const d = new Date(evd.fechaSeguimiento as any);
    return Number.isNaN(d.getTime()) ? "" : "Corte del " + d.toLocaleDateString("es-MX");
  }

  /* ---------- Bitácora legible ---------- */

  private static readonly ETIQUETAS_CAMPO: { [campo: string]: string } = {
    fase_deteccion: "Fase de detección",
    descripcion: "Descripción del riesgo",
    causa_raiz: "Causa raíz",
    prob_inicial: "Probabilidad inicial",
    impacto_inicial: "Impacto inicial",
    estrategia: "Estrategia",
    fecha_identificacion: "Fecha de identificación",
    periodicidad: "Periodicidad",
    acciones: "Acciones a seguir",
    responsable_recursos: "Responsable / recursos",
    prob_reevaluada: "Probabilidad reevaluada",
    impacto_reevaluado: "Impacto reevaluado",
    activos_relacionados: "Activos relacionados",
    seguimiento: "Seguimiento",
    estatus: "Estatus",
    evidencia: "Evidencia adjunta",
    alta: "Alta del riesgo",
  };

  protected readonly etiquetasCampo = ExpedienteRiesgoIAComponent.ETIQUETAS_CAMPO;

  /** Esta bitácora registra también la eliminación del riesgo. */
  override etiquetaAccionBit(accion?: string): string {
    return accion === "ELIMINACION" ? "Eliminación" : super.etiquetaAccionBit(accion);
  }

  /* ---------- Resumen en prosa ---------- */

  resumenLegible(): string {
    const r = this.riesgo;
    const partes: string[] = [];

    partes.push(`El riesgo ${r.consecutivo || "—"} de ${this.nombreMatriz} se evaluó con severidad `
      + `${r.severidadInicial ?? "—"} (${this.etiquetaBanda(r.clasificacionInicial)})`);

    if (r.estrategia) {
      partes.push(`se decidió ${String(r.estrategia).toLowerCase()}`);
    }
    if (r.severidadReevaluada !== null && r.severidadReevaluada !== undefined) {
      partes.push(`tras el plan queda en ${r.severidadReevaluada} `
        + `(${this.etiquetaBanda(r.clasificacionReevaluada)})`);
    }

    if (this.cortes.length) {
      let texto = `lleva ${this.cortes.length} corte(s) de monitoreo`;
      if (this.cortesSinResultado > 0) {
        texto += `, ${this.cortesSinResultado} sin resultado capturado`;
      }
      partes.push(texto);
    } else {
      partes.push("todavía no tiene cortes de monitoreo");
    }

    if (this.evidenciasAplica === "NO") {
      partes.push("las evidencias están declaradas como no aplicables");
    } else if (this.evidencias.length) {
      partes.push(`y respalda su gestión con ${this.evidencias.length} evidencia(s) adjunta(s)`);
    } else {
      partes.push("y aún no tiene evidencias adjuntas");
    }

    return partes.join("; ") + ".";
  }

  /* ---------- Verificación de consistencia ---------- */

  verificaciones(): { ok: boolean; texto: string }[] {
    const r = this.riesgo;
    const lista: { ok: boolean; texto: string }[] = [];

    lista.push(r.acciones && r.acciones.trim()
      ? { ok: true, texto: "Tiene acciones de tratamiento capturadas." }
      : { ok: false, texto: "No tiene acciones de tratamiento capturadas." });

    lista.push(r.responsableRecursos && r.responsableRecursos.trim()
      ? { ok: true, texto: "Tiene responsable asignado." }
      : { ok: false, texto: "No tiene responsable asignado." });

    lista.push(this.cortes.length
      ? { ok: true, texto: `Tiene evidencia de seguimiento (${this.cortes.length} corte(s)).` }
      : { ok: false, texto: "No tiene ningún corte de monitoreo registrado." });

    if (this.cortesSinResultado > 0) {
      lista.push({
        ok: false,
        texto: `${this.cortesSinResultado} corte(s) sin resultado capturado: la fecha pasó y no se anotó qué se encontró.`,
      });
    }

    if (r.severidadReevaluada === null || r.severidadReevaluada === undefined) {
      lista.push({ ok: false, texto: "Falta la reevaluación tras el plan de acción." });
    } else {
      lista.push({ ok: true, texto: "Está reevaluado tras el plan de acción." });
    }

    if (!this.cargandoEvd) {
      if (this.evidenciasAplica === "NO") {
        lista.push({ ok: true, texto: "Evidencias declaradas como No aplica: estado justificado." });
      } else {
        lista.push(this.evidencias.length
          ? { ok: true, texto: "Tiene evidencias adjuntas." }
          : { ok: false, texto: "No tiene evidencias adjuntas ni están marcadas como No aplica." });
      }
    }

    return lista;
  }

  /* ---------- Exportar PDF ---------- */

  protected generarPdf(): void {
    const r = this.riesgo;
    const doc = new PdfExpediente([247, 243, 243]);
    doc.membrete();
    doc.portada({
      titulo: "Expediente del riesgo",
      rubro: "PLAN DE GESTIÓN DE RIESGOS",
      folio: r.consecutivo || "—",
      saltoFolio: 6.5,
      nombre: this.nombreMatriz,
      riesgo: `Severidad inicial: ${r.severidadInicial ?? "—"} (${this.etiquetaBanda(r.clasificacionInicial)})`
        + `   ·   tras el plan: ${r.severidadReevaluada ?? "—"} (${this.etiquetaBanda(r.clasificacionReevaluada)})`,
    });
    doc.resumen(this.resumenLegible(), this.verificaciones());
    this.pdfRiesgo(doc);
    this.pdfCortes(doc);
    this.pdfEvidencias(doc);
    this.pdfBitacora(doc);
    doc.terminar(r.consecutivo || "", `Expediente-${r.consecutivo || "riesgo"}.pdf`);
  }

  private pdfRiesgo(doc: PdfExpediente): void {
    const r = this.riesgo;
    doc.titulo("El riesgo");
    doc.fila("Folio", r.consecutivo || "—");
    doc.fila("Descripción", r.descripcion || "—");
    doc.fila("Causa raíz", r.causaRaiz || "—");
    doc.fila("Fase de detección", r.faseDeteccion || "—");
    doc.fila("Fecha de identificación", fechaPdf(r.fechaIdentificacion));
    doc.fila("Evaluación inicial",
      this.evaluacionPdf(r.probInicial, r.impactoInicial, r.severidadInicial, r.clasificacionInicial));
    doc.fila("Criterio de aceptación", r.criterioAceptacion || "—");
    doc.fila("Estrategia", r.estrategia || "—");
    doc.fila("Acciones a seguir", r.acciones || "—");
    doc.fila("Responsable / recursos", r.responsableRecursos || "—");
    doc.fila("Reevaluación tras el plan",
      this.evaluacionPdf(r.probReevaluada, r.impactoReevaluado, r.severidadReevaluada, r.clasificacionReevaluada));
    doc.fila("Periodicidad de revisión", r.periodicidad || "—");
    if (this.activosCitados.length) {
      doc.fila("Activos relacionados", this.activosCitados.join(", "));
    }
    doc.y += 2;
  }

  /** "probabilidad 3 x impacto 4 = 12 (Alta)". */
  private evaluacionPdf(prob?: number, impacto?: number, severidad?: number, clasificacion?: string): string {
    return `probabilidad ${prob ?? "—"} x impacto ${impacto ?? "—"} = `
      + `${severidad ?? "—"} (${this.etiquetaBanda(clasificacion)})`;
  }

  private pdfCortes(doc: PdfExpediente): void {
    doc.titulo("Cortes de monitoreo");
    if (!this.cortes.length) {
      doc.parrafo("Sin cortes registrados.");
    }
    this.cortes.forEach((s) => {
      doc.fila(fechaPdf(s.fechaMonitoreo),
        (s.resultado || "Sin resultado capturado")
        + (s.usuarioNombre ? ` — registró: ${s.usuarioNombre}` : ""));
    });
    doc.y += 2;
  }

  private pdfEvidencias(doc: PdfExpediente): void {
    doc.titulo("Evidencias adjuntas");
    if (this.evidenciasAplica === "NO") {
      doc.parrafo("Declaradas como NO APLICAN para este riesgo: estado justificado, no un faltante.");
    } else if (!this.evidencias.length) {
      doc.parrafo("Sin evidencias adjuntas.");
    }
    this.evidencias.forEach((e) => {
      doc.evidencia(e, this.corteDe(e) ? ` — respalda el ${this.corteDe(e).toLowerCase()}` : "");
    });
    doc.y += 2;
  }

  private pdfBitacora(doc: PdfExpediente): void {
    doc.titulo("Bitácora de cambios");
    if (!this.historial.length) {
      doc.parrafo("Sin movimientos registrados.");
    }
    this.historial.forEach((h) => {
      const cambio = (h.valorAnterior ? this.valorLegible(h.valorAnterior) + " -> " : "")
        + (h.valorNuevo ? this.valorLegible(h.valorNuevo) : "");
      doc.fila(fechaPdf(h.fechaCambio, true),
        `${this.etiquetaAccionBit(h.accion)} · ${this.etiquetaCampo(h.campoModificado)}`
        + (cambio ? `: ${cambio}` : "")
        + (h.usuarioNombre ? ` — ${h.usuarioNombre}` : ""));
    });
  }
}
